import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import defaultImage from 'shared/components/Article/assets/default.svg';

interface IUseLazyArticleImageParams {
  image?: string;
}

export const useLazyArticleImage = ({ image }: IUseLazyArticleImageParams) => {
  const containerRef = useRef<HTMLAnchorElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    if (src !== defaultImage) {
      setSrc(defaultImage);
    } else {
      setIsImageLoaded(true);
    }
  }, [src]);

  useLayoutEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setIsInView(true);

      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isInView) {
      setSrc(image ?? defaultImage);

      return;
    }

    setSrc(undefined);
    setIsImageLoaded(false);
  }, [image, isInView]);

  useEffect(() => {
    if (!src) {
      return;
    }

    let isCancelled = false;
    const preloader = new Image();
    const safetyTimeout = setTimeout(() => {
      if (!isCancelled) {
        setIsImageLoaded(true);
      }
    }, 10000);

    setIsImageLoaded(false);
    preloader.src = src;

    if (preloader.complete && preloader.naturalWidth > 0) {
      clearTimeout(safetyTimeout);
      setIsImageLoaded(true);

      return () => {
        isCancelled = true;
      };
    }

    preloader.onload = () => {
      if (!isCancelled) {
        setIsImageLoaded(true);
      }
    };

    preloader.onerror = () => {
      if (isCancelled) {
        return;
      }

      if (src !== defaultImage) {
        setSrc(defaultImage);
      } else {
        setIsImageLoaded(true);
      }
    };

    return () => {
      isCancelled = true;
      clearTimeout(safetyTimeout);
      preloader.onload = null;
      preloader.onerror = null;
    };
  }, [src]);

  return {
    src,
    isImageLoaded,
    containerRef,
    onImageLoad: handleImageLoad,
    onImageError: handleImageError,
  };
};
